/**
 * State for a text field that supports `@mentions`.
 *
 * Keeps the chosen assignee as an id alongside the text, and drops it if the
 * handle is edited away — so the visible text and the delegation cannot drift
 * apart.
 *
 * The input ref is owned by the *caller* and passed in, rather than created and
 * returned here. Returning it would mean every component reached a ref through
 * an object property during render, which is exactly the pattern the React
 * Compiler rules reject.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

import type { UserPublic } from '@/lib/api/types';

export type MentionInputRef = RefObject<HTMLInputElement | null>;

export interface MentionSpan {
  query: string;
  start: number;
  end: number;
}

/**
 * Find a `@handle` being typed at the caret.
 *
 * Only matches when the `@` starts a word, so an email address in a title does
 * not open the picker. Returns the fragment and its bounds so the caller can
 * replace exactly that span once a user is chosen.
 */
export function findMentionAtCaret(value: string, caret: number): MentionSpan | null {
  const before = value.slice(0, caret);
  const match = /(?:^|\s)@([a-z0-9_-]*)$/i.exec(before);
  if (!match) return null;

  const fragment = match[1] ?? '';
  return {
    query: fragment,
    start: caret - fragment.length - 1, // include the '@'
    end: caret,
  };
}

export interface MentionField {
  value: string;
  setValue: (value: string) => void;
  assignee: UserPublic | null;
  mention: MentionSpan | null;
  dismissMention: () => void;
  handleChange: (next: string, caret: number) => void;
  selectUser: (user: UserPublic) => void;
  reset: () => void;
}

export function useMentionField(
  inputRef: MentionInputRef,
  initialValue = '',
): MentionField {
  const [value, setValue] = useState(initialValue);
  const [assignee, setAssignee] = useState<UserPublic | null>(null);
  const [mention, setMention] = useState<MentionSpan | null>(null);

  const handleChange = useCallback((next: string, caret: number) => {
    setValue(next);
    setMention(findMentionAtCaret(next, caret));
    setAssignee((current) =>
      // If the handle no longer appears in the text, the delegation it stood
      // for is gone too.
      current && !next.toLowerCase().includes(`@${current.username}`) ? null : current,
    );
  }, []);

  const selectUser = useCallback(
    (user: UserPublic) => {
      if (!mention) return;

      const next = `${value.slice(0, mention.start)}@${user.username} ${value.slice(mention.end)}`;
      const caret = mention.start + user.username.length + 2;

      // Three plain setters. An earlier version nested these inside a
      // setMention updater to avoid depending on `value`; updaters must be
      // pure, and React silently dropped the nested updates — the picker
      // appeared to ignore Enter entirely.
      setValue(next);
      setAssignee(user);
      setMention(null);

      // Restore the caret after the value lands, so typing continues where the
      // handle ended rather than at the start of the field.
      requestAnimationFrame(() => {
        const input = inputRef.current;
        input?.focus();
        input?.setSelectionRange(caret, caret);
      });
    },
    [mention, value, inputRef],
  );

  const reset = useCallback(() => {
    setValue('');
    setAssignee(null);
    setMention(null);
  }, []);

  const dismissMention = useCallback(() => setMention(null), []);

  return useMemo(
    () => ({
      value,
      setValue,
      assignee,
      mention,
      dismissMention,
      handleChange,
      selectUser,
      reset,
    }),
    [value, assignee, mention, dismissMention, handleChange, selectUser, reset],
  );
}

/** Convenience: a mention field with its own input ref, for simple call sites. */
export function useMentionInput(initialValue = '') {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const field = useMentionField(inputRef, initialValue);
  return { field, inputRef };
}
